import { isAxiosError } from 'axios';
import type { ScalarDiffFieldOut } from '@/api/generated/schemas';
import type { OutcomeTone } from '@/components/common/OutcomeTone';

/**
 * How one publication left the sync-review wizard.
 *
 * `refreshed` = the operator applied the upstream state; `auto_refreshed` =
 * the wizard applied a file-only diff on its own; `skipped` = a diff was
 * shown and deliberately left in place; `no_diff` = the wizard walked past
 * it because local and upstream already agree; `not_found` = a catalog scan
 * probed an id with no publication behind it; `failed` = the refresh
 * request errored and the operator moved on.
 */
export type SyncReviewStatus =
  | 'refreshed'
  | 'auto_refreshed'
  | 'skipped'
  | 'no_diff'
  | 'not_found'
  | 'failed';

export interface SyncReviewOutcome {
  publicationId: number;
  status: SyncReviewStatus;
  /** Backend message of a failed refresh, when there was one. */
  error?: string;
}

export const countByStatus = (
  outcomes: SyncReviewOutcome[],
  status: SyncReviewStatus
): number => outcomes.filter((o) => o.status === status).length;

/**
 * Publications the operator still has to do something about: a skip left a
 * known diff in place, a failure left the refresh undone.
 */
export const problemCount = (outcomes: SyncReviewOutcome[]): number =>
  countByStatus(outcomes, 'skipped') + countByStatus(outcomes, 'failed');

/**
 * Share of the walked publications now in line with upstream, 0–100.
 * A probed id with nothing behind it is no publication at all, so it sits
 * outside the fraction rather than dragging it down on a sparse catalog.
 */
export const successPercent = (outcomes: SyncReviewOutcome[]): number => {
  const walked = outcomes.length - countByStatus(outcomes, 'not_found');
  return walked
    ? Math.round(
        ((countByStatus(outcomes, 'refreshed') +
          countByStatus(outcomes, 'auto_refreshed') +
          countByStatus(outcomes, 'no_diff')) /
          walked) *
          100
      )
    : 0;
};

export const statusTone = (status: SyncReviewStatus): OutcomeTone => {
  switch (status) {
    case 'refreshed':
    case 'auto_refreshed':
      return 'success';
    case 'skipped':
      return 'warning';
    case 'failed':
      return 'error';
    case 'no_diff':
    case 'not_found':
      return 'neutral';
  }
};

/** Nothing happened and nothing needs to: dim the row. */
export const isMutedStatus = (status: SyncReviewStatus): boolean =>
  status === 'no_diff' || status === 'not_found';

/**
 * Outcomes re-sorted into queue order. The pipelined walk records
 * background self-resolutions and operator decisions as they happen, so
 * they interleave; the result list should read in the order the queue was
 * walked. Stable: an id missing from the queue sorts first, ties keep
 * their recorded order.
 */
export const walkOrder = (
  outcomes: SyncReviewOutcome[],
  queue: number[]
): SyncReviewOutcome[] => {
  const position = new Map(queue.map((id, index) => [id, index]));
  return [...outcomes].sort(
    (a, b) =>
      (position.get(a.publicationId) ?? 0) -
      (position.get(b.publicationId) ?? 0)
  );
};

/**
 * A group of product fields the operator can let the wizard apply without
 * a human look. Each group is its own checkbox in the wizard.
 *
 * `files` = the LCPS id, the file URLs and the metrics derived from the
 * file itself (duration, page and character counts) — no editorial
 * content. `availability` = the sale window (`available_from`,
 * `available_till`), which the Content Catalog moves routinely.
 */
export type AutoApplyGroup = 'files' | 'availability';

export const AUTO_APPLY_GROUPS: readonly AutoApplyGroup[] = [
  'files',
  'availability',
];

/**
 * Fields per group. They live under a product, so a diff path is
 * `ebook.<field>` or `audio.<field>`.
 */
const AUTO_APPLY_FIELDS: Record<AutoApplyGroup, ReadonlySet<string>> = {
  files: new Set([
    'product_url',
    'demo_url',
    'preview_url',
    'lcps_content_id',
    'duration',
    'pages_count',
    'chars_count',
  ]),
  availability: new Set(['available_from', 'available_till']),
};

/**
 * A diff the wizard may apply on its own: every changed field belongs to
 * one of the enabled groups, and none of them was manually edited — a
 * manual edit is a human decision the auto-apply must not silently walk
 * past. A locked field disqualifies too: the apply cannot write it, so the
 * diff would stay open. With no group enabled nothing qualifies.
 */
export const isAutoApplicableDiff = (
  fields: ScalarDiffFieldOut[],
  groups: Iterable<AutoApplyGroup>
): boolean => {
  const enabled = [...groups].map((group) => AUTO_APPLY_FIELDS[group]);
  return (
    enabled.length > 0 &&
    fields.length > 0 &&
    fields.every((field) => {
      if (field.manually_edited || field.import_locked) return false;
      const name = field.path.replace(/^(ebook|audio)\./, '');
      return enabled.some((set) => set.has(name));
    })
  );
};

/**
 * The id a catalog scan probed has no publication behind it — the walk
 * steps over it instead of stopping on the error.
 */
export const isNotFoundError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

/**
 * Minimum spacing between the starts of two consecutive requests, keeping
 * the walk within the production rate limit instead of chaining responses
 * back-to-back.
 */
export const REQUEST_INTERVAL_MS = 500;

/**
 * How many fetched-but-undecided diffs the walk may hold. Prefetching runs
 * ahead of the operator so the next diff is usually already there, but
 * stops at this many pending reviews — beyond that the requests would be
 * spent on work the operator may never reach.
 */
export const MAX_PENDING_DIFFS = 10;

/**
 * How long to hold the next request so request starts stay at least
 * `interval` apart. The time the previous request itself took counts
 * toward the budget — after a slow response there is no extra wait.
 */
export const nextRequestDelay = (
  lastRequestAt: number,
  now: number,
  interval = REQUEST_INTERVAL_MS
): number => Math.max(0, interval - (now - lastRequestAt));

/**
 * The most ids one scan may walk — an upper bound comfortably above any
 * realistic catalog, guarding the id-array allocation against a typo in
 * the range bounds.
 */
export const MAX_SCAN_RANGE = 100_000;

/** How many ids the inclusive range covers; 0 when the bounds are bad. */
export const scanRangeLength = (from: number, to: number): number =>
  !Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < from
    ? 0
    : to - from + 1;

/**
 * The inclusive id range a catalog scan walks; empty when the bounds are
 * bad or the range exceeds `MAX_SCAN_RANGE`. Call it when the walk starts,
 * not on every render — the array is as long as the range.
 */
export const scanQueue = (from: number, to: number): number[] => {
  const length = scanRangeLength(from, to);
  if (length === 0 || length > MAX_SCAN_RANGE) return [];
  return Array.from({ length }, (_, i) => from + i);
};
